import "./AdminBottomNavConfig.scss";

import React, { useEffect, useMemo, useState } from "react";

import HallAppToast from "../commons/HallAppToast";
import { menuItems } from "./Sidebar";

const LS_KEY = "hallapp-admin-bottom-nav-config";
const MAX_ITEMS = 3;

const FIXED_PATH = "/config-bottom-menu";
const DEFAULT_PATHS = ["/relatorio-geral", "/comandas", "/agenda"];

export default function AdminBottomNavConfig() {
  const [enabled, setEnabled] = useState(true);
  const [selectedPaths, setSelectedPaths] = useState(DEFAULT_PATHS);
  const [isSavingHard, setIsSavingHard] = useState(false);

  // ========= helpers =========
  const safeParse = (json, fallback) => {
    try {
      const v = JSON.parse(json);
      return v ?? fallback;
    } catch {
      return fallback;
    }
  };

  const normalizePath = (p) => {
    if (!p) return "";
    const s = String(p).trim();
    if (!s) return "";
    return s.startsWith("/") ? s : `/${s}`;
  };

  const flattenMenu = (items) => {
    const out = [];
    (items || []).forEach((it) => {
      if (it?.children?.length) {
        out.push(...flattenMenu(it.children));
      } else if (it?.isRoute && it?.path) {
        out.push(it);
      }
    });
    return out;
  };

  const groupBySection = (itemsFlat) => {
    const map = new Map();
    for (const it of itemsFlat) {
      const section = it.section || "Outros";
      if (!map.has(section)) map.set(section, []);
      map.get(section).push(it);
    }
    return Array.from(map.entries()).map(([section, items]) => ({
      section,
      items: items.sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    }));
  };

  const loadConfigRaw = () => {
    const fallback = { enabled: true, selectedPaths: DEFAULT_PATHS };
    const raw = localStorage.getItem(LS_KEY);
    const cfg = raw ? safeParse(raw, fallback) : fallback;

    const selectedRaw = Array.isArray(cfg.selectedPaths) ? cfg.selectedPaths : DEFAULT_PATHS;

    return {
      enabled: cfg.enabled !== false,
      selectedPaths: selectedRaw.map(normalizePath).filter(Boolean),
    };
  };

  const saveConfig = (cfg) => {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  };

  async function clearAppCacheAndReload() {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }

      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }

      window.location.reload();
    } catch (e) {
      console.error("Falha ao limpar cache do app:", e);
      window.location.reload();
    }
  }

  // ========= data =========
  const flatRoutes = useMemo(() => flattenMenu(menuItems), []);

  const selectableRoutes = useMemo(() => {
    return flatRoutes
      .map((it) => ({
        ...it,
        path: it?.path ? normalizePath(it.path) : it?.path,
      }))
      .filter((it) => it?.path && it.path !== FIXED_PATH);
  }, [flatRoutes]);

  const grouped = useMemo(() => groupBySection(selectableRoutes), [selectableRoutes]);

  // ✅ SET de paths válidos (isso resolve o bug de não conseguir selecionar outro)
  const validSelectableSet = useMemo(() => {
    return new Set(selectableRoutes.map((it) => it.path));
  }, [selectableRoutes]);

  const selectedSet = useMemo(() => new Set(selectedPaths), [selectedPaths]);
  const canSelectMore = selectedPaths.length < MAX_ITEMS;

  // ========= init (carrega e SANEIA) =========
  useEffect(() => {
    const cfg = loadConfigRaw();

    // remove fixo, remove inválidos, limita em 3
    const cleaned = (cfg.selectedPaths || [])
      .map(normalizePath)
      .filter(Boolean)
      .filter((p) => p !== FIXED_PATH)
      .filter((p) => validSelectableSet.has(p))
      .slice(0, MAX_ITEMS);

    // fallback pro default (mas também saneia: só deixa os que existem)
    const defaultCleaned = DEFAULT_PATHS.map(normalizePath)
      .filter((p) => p !== FIXED_PATH)
      .filter((p) => validSelectableSet.has(p))
      .slice(0, MAX_ITEMS);

    const finalSelected = cleaned.length ? cleaned : defaultCleaned.length ? defaultCleaned : [];

    setEnabled(cfg.enabled);
    setSelectedPaths(finalSelected);

    // persiste já saneado
    saveConfig({ enabled: cfg.enabled, selectedPaths: finalSelected });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validSelectableSet]);

  // ========= actions =========
  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    saveConfig({ enabled: next, selectedPaths });
  };

  const togglePath = (path) => {
    const p = normalizePath(path);

    if (!p) return;
    if (p === FIXED_PATH) return;
    if (!enabled) return;

    let next = [...selectedPaths];

    if (selectedSet.has(p)) {
      next = next.filter((x) => x !== p);
    } else {
      // ✅ só entra se for válido mesmo
      if (!validSelectableSet.has(p)) return;
      if (!canSelectMore) return;
      next.push(p);
    }

    // saneia novamente
    next = next
      .map(normalizePath)
      .filter(Boolean)
      .filter((x) => x !== FIXED_PATH)
      .filter((x) => validSelectableSet.has(x))
      .slice(0, MAX_ITEMS);

    // se zerar tudo, volta pro padrão (saneado)
    if (next.length === 0) {
      const fallback = DEFAULT_PATHS.map(normalizePath)
        .filter((x) => validSelectableSet.has(x))
        .slice(0, MAX_ITEMS);
      next = fallback;
    }

    setSelectedPaths(next);
    saveConfig({ enabled, selectedPaths: next });
  };

  const setDefault3 = () => {
    const next = DEFAULT_PATHS.map(normalizePath)
      .filter((x) => validSelectableSet.has(x))
      .slice(0, MAX_ITEMS);

    setSelectedPaths(next);
    saveConfig({ enabled, selectedPaths: next });
  };

  const clearAll = () => {
    // mantém padrão (não deixa vazio)
    setDefault3();
  };

  const handleSaveAndReload = async () => {
    try {
      setIsSavingHard(true);

      // salva saneado
      const payload = {
        enabled,
        selectedPaths: selectedPaths
          .map(normalizePath)
          .filter((p) => p !== FIXED_PATH)
          .filter((p) => validSelectableSet.has(p))
          .slice(0, MAX_ITEMS),
      };

      saveConfig(payload);
      HallAppToast.success("Configurações salvas. Atualizando...");

      setTimeout(async () => {
        await clearAppCacheAndReload();
      }, 200);
    } catch (e) {
      console.error("Erro ao salvar config bottom menu:", e);
      HallAppToast.genericError("Não foi possível salvar.");
      setIsSavingHard(false);
    }
  };

  // ========= UI =========
  return (
    <div className="admin-bottom-nav-config">
      <div className="admin-bottom-nav-config__card">
        <div className="admin-bottom-nav-config__header">
          <div>
            <h5 className="admin-bottom-nav-config__title">Personalizar Menu Bottom</h5>
            <p className="admin-bottom-nav-config__subtitle">
              Ative/desative e escolha até <strong>{MAX_ITEMS}</strong> itens para aparecerem no rodapé.
              <br />
              <strong>Config.</strong> (<code>{FIXED_PATH}</code>) é fixo e sempre aparece.
            </p>
          </div>

          {/* Toggle */}
          <button
            type="button"
            className={`admin-toggle ${enabled ? "admin-toggle--on" : "admin-toggle--off"}`}
            onClick={toggleEnabled}
            role="switch"
            aria-checked={enabled}
            aria-label={enabled ? "Desativar menu inferior" : "Ativar menu inferior"}
          >
            <span className="admin-toggle__track">
              <span className="admin-toggle__thumb" />
            </span>
            <span className="admin-toggle__label">{enabled ? "Habilitado" : "Desabilitado"}</span>
          </button>
        </div>

        <div className="admin-bottom-nav-config__actions">
          <button type="button" className="btn-ghost" onClick={setDefault3}>
            Usar padrão (3)
          </button>

          <button type="button" className="btn-ghost danger" onClick={clearAll}>
            Resetar seleção
          </button>

          <div className="admin-bottom-nav-config__count">
            Selecionados: <strong>{selectedPaths.length}</strong>/{MAX_ITEMS}
          </div>

          <button
            type="button"
            className="btn-save"
            onClick={handleSaveAndReload}
            disabled={isSavingHard}
            title="Salvar e aplicar"
          >
            {isSavingHard ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>

        {!enabled && (
          <div className="admin-bottom-nav-config__warn">
            O menu bottom está desabilitado. Ative para escolher os itens.
          </div>
        )}

        {enabled && selectedPaths.length >= MAX_ITEMS && (
          <div className="admin-bottom-nav-config__warn">
            Para selecionar outro menu, desmarque 1 item antes.
          </div>
        )}

        <div className="admin-bottom-nav-config__list">
          {grouped.map((g) => (
            <div key={g.section} className="admin-bottom-nav-config__section">
              <div className="admin-bottom-nav-config__sectionTitle">{g.section}</div>

              <div className="admin-bottom-nav-config__grid">
                {g.items.map((it) => {
                  const path = it.path;
                  const checked = selectedSet.has(path);

                  // bloqueia marcar novos quando chegou no limite, mas permite desmarcar os já selecionados
                  const disabled = !enabled || (!checked && !canSelectMore);

                  return (
                    <label
                      key={path}
                      className={`menu-check ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}`}
                      title={disabled && !checked ? `Limite de ${MAX_ITEMS} itens` : ""}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => togglePath(path)}
                      />

                      <div className="menu-check__content">
                        <div className="menu-check__label">
                          {it.label}
                          {it.validateAdmin ? <span className="pill">admin</span> : null}
                          {it.isNew ? <span className="pill new">novo</span> : null}
                        </div>

                        {/* ajuda debug: mostra o path real */}
                        <div className="menu-check__path">{path}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-bottom-nav-config__bottomBar">
        <button
          type="button"
          className="btn-save w-100"
          onClick={handleSaveAndReload}
          disabled={isSavingHard}
        >
          {isSavingHard ? "Salvando..." : "Salvar alterações"}
        </button>
      </div>

      <div style={{ height: "calc(64px + 90px + env(safe-area-inset-bottom))" }} />
    </div>
  );
}
